import { useState, useEffect, useRef } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { LayoutDashboard, Users, ReceiptText, CalendarDays, TrendingUp, TrendingDown, Layers, Package, UserCog, Building2, LogOut, FileText, FileClock, Clock, Settings, Settings2, ShoppingBag, Smartphone, BarChart3, Target, Menu, X, Trophy, ClipboardList, Map as MapIcon, TableProperties, Sparkles, GitBranch, Camera, FolderArchive, ScrollText, PackageCheck, Search, Landmark, Plus, HandCoins, Boxes, GitCompare, FileDiff, ShieldCheck, Coins, EyeOff, PackagePlus, Receipt, CalendarClock, RotateCcw, Megaphone, GraduationCap, UserX, Undo2, Trash2, Wallet, Ticket, ListChecks, Tags, FileBarChart,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { ALAMAT_DASHBOARD_SALES, adalahKaryawanMurni, bolehBukaDashboardInventory } from "../../lib/halamanAwal";
import { useApakahDesktop } from "../../hooks/useApakahDesktop";
import EmployeeAvatar from "./EmployeeAvatar";
import NotificationBell from "./NotificationBell";
import CommandPalette from "./CommandPalette";
// Query key & param diimpor langsung dari modul halaman yang sudah dimigrasi ke
// React Query — satu-satunya sumber kebenaran, supaya prefetch di sini TIDAK
// PERNAH diam-diam melenceng dari key asli yang dipakai halamannya (kalau
// halaman ganti key tapi prefetch di sini lupa diikutkan, cache prefetch jadi
// mubazir — tidak pernah kena, bukan bug fatal, tapi percuma).
import { PRODUK_QK, OPSI_QK, PRODUK_DEFAULT_PARAMS } from "../../pages/InventoryProducts";
import { DASHBOARD_QK, DASHBOARD_FINANCE_QK, DASHBOARD_AKTIVITAS_QK } from "../../pages/Dashboard";
import { INVENTORY_DASHBOARD_QK } from "../../pages/InventoryDashboard";
import { MY_PROFILE_QK, MY_CUTI_QK, MY_SLIPS_QK } from "../../pages/MyDashboard";
import { EMPLOYEES_QK } from "../../pages/Employees";

const adminNav = [
  // Pusat Laporan (brief owner 10 Sep 2026 poin 2) — satu pintu ke SEMUA laporan
  // sistem. Section baru, TIDAK menggeser satu pun menu yang sudah ada.
  //
  // Halamannya sendiri tidak punya kunci izin (isinya disaring per-laporan di
  // backend), tapi menunya tetap digerbangi daftar `mods` di bawah: tanpa itu,
  // 61 karyawan yang tidak punya izin laporan apa pun akan melihat menu yang
  // SELALU kosong saat dibuka. Daftar ini = kunci modul yang benar-benar dipakai
  // entri katalog (`backend/routers/laporan/registry.py`); kalau di sana ditambah
  // kunci baru yang tidak ada di sini, menunya cuma tidak muncul untuk pemegang
  // kunci itu — bukan lubang izin.
  { section: "Laporan", items: [
    { to: "/laporan", icon: FileBarChart, label: "Pusat Laporan", tid: "nav-pusat-laporan",
      mods: ["finance_laporan", "finance_beban", "finance_piutang", "finance_utang",
             "finance_kas_bank", "finance_petty", "finance_persediaan", "finance_audit",
             "finance_rekonsiliasi", "finance_cash_opname", "finance_reimbursement",
             "inventory_laporan", "inventory_produk", "inventory_po", "inventory_unit",
             "sales", "sales_report", "acquisitions", "rekon_sales", "loss_customer",
             "waiting_list", "servis", "payroll", "performance", "attendance",
             "pelatihan", "marketing_campaign", "marketing_executive", "marketing_ads"] },
  ]},
  // Finance dinaikkan paling atas (brief owner 19 Agu poin 7: "agar mudah dicari"),
  // di atas Marketing yang sebelumnya duduk lebih dulu. Murni urutan tampil — kunci
  // izin, gate (FinanceGate di App.js), dan modPrefix di bawah TIDAK berubah.
  { section: "Finance", items: [
    // Tanpa penjagaan modul, item ini tampil untuk SEMUA orang lalu FinanceGate di App.js
    // memantulkan mereka balik ke dashboard tanpa pesan apa pun — terasa seperti menu rusak.
    // modPrefix dipakai karena gerbangnya meloloskan siapa pun yang punya minimal satu izin
    // finance_*, jadi mengunci ke satu module key saja justru menutup jalan masuk yang sah.
    { to: "/finance", icon: Landmark, label: "Finance", tid: "nav-finance", quickAdd: "/finance/catat",
      mods: ["finance", "finance_dashboard"], modPrefix: "finance_" },
  ]},
  // Marketing (brief owner 12 Agu poin 4): sidebar utama cuma menampilkan SATU
  // entri, sama seperti Finance — 13 menu Marketing pindah ke sidebar sendiri di
  // `pages/marketing/MarketingLayout.jsx`, dirender lewat MarketingGate di App.js.
  // `mods` di sini daftar EKSPLISIT (bukan modPrefix umum) karena harus
  // MENGECUALIKAN `marketing_margin` — kunci itu bukan halaman, murni penentu
  // tampil-tidaknya angka margin/ROI di dalam halaman lain, dan owner tegas: kalau
  // ikut jadi syarat masuk, orang yang cuma dicentang lihat-margin bisa membuka
  // menu Marketing padahal tidak berhak atas satu pun halamannya.
  { section: "Marketing", items: [
    { to: "/marketing", icon: Megaphone, label: "Marketing", tid: "nav-marketing",
      mods: ["marketing", "marketing_dashboard", "marketing_campaign", "marketing_kol", "marketing_pfp",
             "marketing_ads", "marketing_lead", "marketing_promo", "marketing_content", "marketing_budget",
             "marketing_expense", "marketing_executive"] },
  ]},
  { section: "Inventory", items: [
    // G8 uji coba: tampil kalau backend meloloskan MINIMAL SATU bagian. Predikatnya
    // (`lib/halamanAwal.js`) dipakai JUGA oleh aturan halaman awal — satu sumber, supaya
    // tidak ada orang yang dialihkan ke dashboard yang menunya tidak tampil, atau
    // sebaliknya. Ia juga meloloskan peninjau Request Stok yang cuma punya `update`,
    // persis gerbang `dashboard_inventory.py`.
    { to: "/inventory/dashboard", icon: LayoutDashboard, label: "Dashboard Inventory", tid: "nav-dashboard-inventory",
      boleh: bolehBukaDashboardInventory },
    { to: "/inventory/produk", icon: Package, label: "Master Produk", tid: "nav-inv-produk", mod: "inventory_produk" },
    // Label "Rakit Bundling" basi sejak 23 Agu 2026 (rakit/urai fisik
    // dimatikan) — isinya sekarang resep & harga PAKET JUAL (brief 22 Sep F1).
    // tid tidak diubah.
    { to: "/inventory/bundling", icon: Layers, label: "Paket Bundling", tid: "nav-inv-bundling", mod: "inventory_produk" },
    // Rangkuman Price List (brief owner 10 Sep 2026 poin 1). Kuncinya sama
    // dengan Master Produk — halaman ini menampilkan harga jual yang sama,
    // cuma dirangkum bersama posisi stok. Angka modal/margin ikut aturan dasar
    // `inventory_laporan.view` DITAMBAH pengecualian per akun yang Bos atur
    // lewat tombol "Atur Akses Modal & Margin" di halaman ini (superadmin
    // saja, `routers/akses_modal.py` + `routers/inventory/akses_modal_
    // pengaturan.py`, brief owner 18 Sep 2026 batch P) — jadi staf sales yang
    // cuma dipercayai data produk tetap boleh membuka menu ini, dan Bos bisa
    // memberi/mencabut modal per akun tanpa mengubah kunci modul ini.
    { to: "/inventory/price-list", icon: Tags, label: "Price List", tid: "nav-inv-price-list", mod: "inventory_produk" },
    { to: "/inventory/pembelian", icon: ClipboardList, label: "Pembelian & Terima", tid: "nav-inv-po", mod: "inventory_po" },
    { to: "/supplier", icon: Building2, label: "Supplier", tid: "nav-supplier", mod: "inventory_po" },
    { to: "/inventory/rekon-po", icon: GitCompare, label: "Rekonsiliasi PO", tid: "nav-inv-rekon-po", mod: "inventory_po" },
    { to: "/multi-rekon", icon: FileDiff, label: "Multi Rekon", tid: "nav-multirekon", mod: "multirekon" },
    // Halaman Stok & Gudang berisi lima modul backend sekaligus (laporan, gudang, unit,
    // transfer, opname/cutover). Menggerbanginya dengan inventory_laporan saja menyembunyikan
    // menu dari orang yang cuma dipercayai transfer atau data unit — padahal halamannya
    // memang menampilkan bagian itu untuk mereka.
    // Kartu Stok (brief owner 19 Agu poin 4): dulu entri terpisah di sini, sekarang
    // sebuah tab DI DALAM halaman Stok & Gudang (lihat InventoryStok.jsx) karena
    // "masih berkaitan erat" — bukan dihapus, cuma tidak lagi butuh pintu masuk
    // sidebar sendiri. Rute lama `/inventory/kartu-stok` masih hidup lewat redirect
    // di App.js (RedirectKartuStok) supaya tautan/bookmark lama tidak mati.
    { to: "/inventory/stok", icon: Boxes, label: "Stok & Gudang", tid: "nav-inv-stok",
      mods: ["inventory_laporan", "inventory_gudang", "inventory_unit", "inventory_transfer", "inventory_opname"] },
    { to: "/inventory/opname-blind", icon: EyeOff, label: "Blind Opname", tid: "nav-opname-blind", mod: "inventory_opname" },
    // Rekon Unit vs Kartu Stok (brief owner 31 Agu poin 2). Ditaruh di sebelah
    // Blind Opname karena keduanya menjawab pertanyaan yang sama dari dua arah:
    // opname membandingkan sistem dengan BARANG FISIK, rekon ini membandingkan
    // ketiga catatan sistem SATU SAMA LAIN.
    { to: "/inventory/rekon-unit", icon: ShieldCheck, label: "Rekon Unit vs Kartu Stok", tid: "nav-rekon-unit", mod: "inventory_unit" },
    // Brief 4 Sep 2026 (P13/P16): approver yang HANYA dicentang kunci approval-nya
    // tetap harus melihat menunya — pola `mods` seperti "Stok & Gudang" di atas.
    { to: "/internal-memo", icon: FileText, label: "Internal Memo", tid: "nav-internal-memo", mods: ["inventory_opname", "internal_memo"] },
    { to: "/request-stok", icon: PackagePlus, label: "Request Stok", tid: "nav-request-stok", mods: ["stock_request", "stock_request_approval"] },
    // Format nomor dokumen, ambang harga wajar, ambang approval PO, & notifikasi
    // stok kritis — sama izinnya dengan Pembelian & Terima (inventory_po) karena
    // pengaturan ini mengubah PERILAKU alur PO/penerimaan, bukan modul terpisah.
    { to: "/inventory/pengaturan", icon: Settings2, label: "Pengaturan Inventory", tid: "nav-inv-pengaturan", mod: "inventory_po" },
    // Brief owner 17 Agu poin 13: "tidak ditemukan menu retur ke supplier ...
    // di seluruh modul Inventory" — fiturnya SUDAH ada (routers/retur.py,
    // halaman /retur tab "pembelian"), cuma menunya duduk di grup Penjualan/POS
    // dengan izin `retur` (bukan `inventory_*`), jadi tidak pernah terlihat dari
    // sini. Ini murni navigasi: TIDAK membangun ulang fitur, TIDAK memindah
    // endpoint, TIDAK mengganti kunci izin — cuma menambah pintu masuk kedua yang
    // menaut langsung ke tab pembelian (Retur.jsx membaca `?tab=` sekarang, lihat
    // berkas itu). Gerbangnya kunci `retur` yang SUNGGUHAN (bukan inventory_po)
    // supaya tidak ada yang melihat menu lalu mentok 403 (aturan #3 CLAUDE.md).
    { to: "/retur?tab=pembelian", icon: Undo2, label: "Retur ke Supplier", tid: "nav-inv-retur-pembelian", mod: "retur" },
    // Entri "Nota Kredit Supplier" (routers/credit_note.py) yang sebelumnya ada di
    // sini DIHAPUS (brief owner 19 Agu poin 1) — halaman itu sudah punya menunya
    // sendiri di sidebar Finance (pages/finance/FinanceLayout.jsx), jadi entri ini
    // murni duplikat, bukan pintu masuk kedua yang perlu. "Retur ke Supplier" di
    // atas TETAP ADA — bukan duplikat, halaman Retur hidup di grup Penjualan/POS.
  ]},
  { section: "Portal Karyawan", items: [
    { to: "/my-attendance", icon: Clock, label: "Absensi Saya", tid: "nav-my-attendance", mod: "my_attendance" },
    { to: "/my-slips", icon: ReceiptText, label: "Slip Gaji Saya", tid: "nav-my-slips", mod: "my_slip" },
    { to: "/my-cuti", icon: CalendarDays, label: "Cuti Saya", tid: "nav-my-cuti", mod: "my_cuti" },
    { to: "/my-kpi", icon: Target, label: "KPI Saya", tid: "nav-my-kpi", mod: "my_kpi" },
    { to: "/my-poin", icon: Coins, label: "Poin & Target Saya", tid: "nav-my-poin", mod: "my_poin" },
    { to: "/my-reimbursement", icon: HandCoins, label: "Reimbursement Saya", tid: "nav-my-reimbursement", mod: "my_reimbursement" },
    { to: "/my-profile", icon: Settings, label: "Profil Saya", tid: "nav-my-profile", mod: "my_profile" },
  ]},
  { section: "POS & Sales", items: [
    // Menunjuk alias, bukan "/": "/" yang dibuka di tab baru atau dari bookmark adalah
    // KEDATANGAN dan dialihkan ke halaman awal akun (G8 butir b). `alias` membuat menu
    // tetap aktif dan judul tab tetap benar saat Sales dirender di "/".
    { to: ALAMAT_DASHBOARD_SALES, alias: ["/"], icon: LayoutDashboard, label: "Dashboard Sales & POS", tid: "nav-dashboard", mod: "dashboard" },
    { to: "/ai", icon: Sparkles, label: "AI Assistant", tid: "nav-ai", mod: "ai_assistant" },
    { to: "/input-nota", icon: ScrollText, label: "Input Nota (Sales)", tid: "nav-nota", mod: "nota_sales" },
    { to: "/down-payment", icon: Wallet, label: "Down Payment", tid: "nav-down-payment", mod: "down_payment" },
    { to: "/input-nota-akuisisi", icon: PackageCheck, label: "Input Nota Akuisisi", tid: "nav-nota-akq", mod: "nota_akuisisi" },
    { to: "/sales", icon: ShoppingBag, label: "Input Penjualan", tid: "nav-sales", mod: "sales" },
    { to: "/rekon-sales", icon: TableProperties, label: "Rekon Sales Harian", tid: "nav-rekon", mod: "rekon_sales" },
    { to: "/rekon-akuisisi", icon: TableProperties, label: "Rekon Akuisisi Unit", tid: "nav-rekon-akq", mod: "rekon_sales" },
    { to: "/acquisitions", icon: Smartphone, label: "Akuisisi Unit", tid: "nav-acq", mod: "acquisitions" },
    { to: "/sales-targets", icon: Target, label: "Target vs Realisasi", tid: "nav-targets", mod: "sales_targets" },
    { to: "/poin-konfigurasi", icon: Coins, label: "Konfigurasi Poin", tid: "nav-poin-konfigurasi", mod: "sales_targets" },
    { to: "/kpi-custom", icon: Target, label: "KPI Non-Sales", tid: "nav-kpi", mod: "sales_targets" },
    { to: "/leaderboard", icon: Trophy, label: "Podium Sales", tid: "nav-leaderboard", mod: "leaderboard" },
    { to: "/sales-report", icon: BarChart3, label: "Laporan Upselling", tid: "nav-upsell", mod: "sales_report" },
    { to: "/waiting-list", icon: FileClock, label: "Waiting List", tid: "nav-waiting-list", mod: "waiting_list" },
    // Loss Customer: pengunjung yang keluar TANPA membeli. Diisi usher/security.
    // Preset "usher" hanya punya aksi `create` — mereka bisa mengisi form tapi tidak
    // bisa memanggil endpoint baca sama sekali, jadi menu ini memang tidak muncul
    // untuk mereka. Itu disengaja: yang mengisi tidak perlu melihat rekap seluruh cabang.
    { to: "/loss-customer", icon: UserX, label: "Loss Customer", tid: "nav-loss-customer", mod: "loss_customer" },
    // Gerbang menunya SENGAJA "view ATAU create": usher/FL biasanya hanya dapat
    // `create` (mereka menerbitkan nomor & melayani, tidak melihat rekap).
    // Kalau digerbangi `view` saja, mereka tidak akan pernah bisa membuka
    // layar yang justru mereka yang pakai sepanjang hari.
    { to: "/antrian", icon: Ticket, label: "Antrian Pelanggan", tid: "nav-antrian",
      modAtauCreate: "antrian" },
    { to: "/pre-order", icon: CalendarClock, label: "List Pre-Order", tid: "nav-preorder", mod: "preorder" },
    // Label lama "Retur" saja tidak bilang halaman ini juga menangani retur
    // KE SUPPLIER (brief owner 17 Agu poin 13 — owner tidak menemukan menu retur
    // pembelian karena label ini tidak memberi isyarat apa pun). Halamannya TIDAK
    // dipindah/diduplikasi — cuma dibuat jujur, dan sekarang ditautkan juga dari
    // grup Inventory di bawah (entri "Retur ke Supplier").
    { to: "/retur", icon: RotateCcw, label: "Retur Penjualan & Pembelian", tid: "nav-retur", mod: "retur" },
    { to: "/csat-hasil", icon: TrendingUp, label: "CSAT Pelanggan", tid: "nav-csat", mod: "csat" },
    { to: "/csat-pengaturan", icon: Settings2, label: "Pengaturan CSAT", tid: "nav-csat-pengaturan", mod: "csat" },
    { to: "/servis", icon: UserCog, label: "Service Center", tid: "nav-servis", mod: "servis" },
    { to: "/pelanggan", icon: Users, label: "Data Pelanggan", tid: "nav-pelanggan", mod: "pelanggan" },
  ]},
  { section: "HRIS", items: [
    // G8 uji coba: gerbangnya `dashboard` — SAMA dengan backend `GET /dashboard`
    // yang menyuplai halamannya (bukan kunci HRIS mana pun, supaya tidak ada
    // menu yang ujungnya 403).
    { to: "/dashboard/hris", icon: LayoutDashboard, label: "Dashboard HRIS", tid: "nav-dashboard-hris", mod: "dashboard" },
    { to: "/employees", icon: Users, label: "Data Karyawan", tid: "nav-employees", mod: "employees" },
    { to: "/org-chart", icon: GitBranch, label: "Bagan Struktur", tid: "nav-org", mod: "org_chart" },
    { to: "/attendance", icon: Clock, label: "Absensi", tid: "nav-attendance", mod: "attendance" },
    { to: "/attendance-review", icon: Camera, label: "Review Selfie", tid: "nav-att-review", mod: "attendance_review" },
    // Kunci izin `attendance` yang SAMA dengan menu "Absensi" di atas (bukan
    // modul baru) — lingkup data & gerbang delete/update-nya harus identik
    // dengan yang membatasi GET /attendance, lihat routers/attendance.py.
    { to: "/attendance-foto", icon: Trash2, label: "Retensi Foto Absensi", tid: "nav-att-foto", mod: "attendance" },
    { to: "/live-map", icon: MapIcon, label: "Peta Live Absensi", tid: "nav-livemap", mod: "live_map" },
    { to: "/payroll", icon: ReceiptText, label: "Payroll / Slip Gaji", tid: "nav-payroll", mod: "payroll" },
    { to: "/rekap-payroll", icon: FileText, label: "Rekap Payroll", tid: "nav-rekap", mod: "rekap_payroll" },
    { to: "/cuti", icon: CalendarDays, label: "Cuti Karyawan", tid: "nav-cuti", mod: "cuti" },
    { to: "/documents", icon: FolderArchive, label: "Dokumen & File", tid: "nav-documents", mod: "documents" },
    { to: "/projects", icon: ClipboardList, label: "Projects & Tugas", tid: "nav-projects", mod: "projects" },
    { to: "/pelatihan", icon: GraduationCap, label: "Pelatihan Karyawan", tid: "nav-pelatihan", mod: "pelatihan" },
    { to: "/performance", icon: TrendingUp, label: "Laporan Performa", tid: "nav-performance", mod: "performance" },
    { to: "/kontrak", icon: FileClock, label: "Kontrak Reminder", tid: "nav-kontrak", mod: "kontrak" },
  ]},
  { section: "Admin", items: [
    { to: "/master", icon: Layers, label: "Master Data", tid: "nav-master", mod: "master" },
    { to: "/divisions", icon: Building2, label: "Master Divisi", tid: "nav-divisions", mod: "master" },
    { to: "/assets", icon: Package, label: "Aset", tid: "nav-assets", mod: "assets" },
    { to: "/approval-center", icon: ShieldCheck, label: "Pusat Approval", tid: "nav-approval-center", mod: "approval_center" },
    { to: "/pengaturan-faktur", icon: Receipt, label: "Template Faktur", tid: "nav-faktur", mod: "faktur" },
  ]},
];

const employeeNav = [
  { section: "Portal Karyawan", items: [
    { to: "/", icon: LayoutDashboard, label: "Beranda", tid: "nav-my-dashboard" },
    { to: "/my-attendance", icon: Clock, label: "Absensi Saya", tid: "nav-my-attendance" },
    { to: "/my-slips", icon: ReceiptText, label: "Slip Gaji Saya", tid: "nav-my-slips" },
    { to: "/my-cuti", icon: CalendarDays, label: "Cuti Saya", tid: "nav-my-cuti" },
    { to: "/my-kpi", icon: Target, label: "KPI Saya", tid: "nav-my-kpi" },
    { to: "/my-poin", icon: Coins, label: "Poin & Target Saya", tid: "nav-my-poin" },
    { to: "/my-reimbursement", icon: HandCoins, label: "Reimbursement Saya", tid: "nav-my-reimbursement" },
    { to: "/projects", icon: ClipboardList, label: "Tugas Saya", tid: "nav-my-projects" },
    // Tanpa `mod` — sama seperti "Tugas Saya" di atasnya. Pembatasan datanya ada di
    // backend: karyawan otomatis hanya melihat pelatihan yang ia ikuti.
    { to: "/pelatihan", icon: GraduationCap, label: "Pelatihan Saya", tid: "nav-my-pelatihan" },
    // Tanpa `mod`, alasan yang sama: staf penerima task Marketing tidak punya
    // izin modul marketing apa pun, dan backend sudah mempersempit isinya ke
    // task miliknya sendiri. Tanpa entri ini, satu-satunya jalan menuju tugasnya
    // adalah notifikasi — dan itu pun buntu sebelum 8 Sep 2026.
    { to: "/marketing/tasks", icon: ListChecks, label: "Task Marketing Saya", tid: "nav-my-mkt-task" },
    { to: "/my-profile", icon: Settings, label: "Pengaturan", tid: "nav-my-profile" },
  ]},
];

export default function Layout() {
  // branding datang dari AuthContext — satu-satunya pemanggil GET /config/branding,
  // supaya tiap muat halaman tidak menembak endpoint yang sama 2-3 kali (brief bos).
  const { user, logout, can, branding } = useAuth();
  const nav2 = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  // Logout lewat tombol sidebar ini navigasi client-side (bukan full page reload),
  // jadi QueryClient (satu instance untuk seluruh tab, lihat src/index.js) TIDAK
  // otomatis kosong. Tanpa `.clear()`, akun berikutnya yang login di tab yang sama
  // bisa sekilas melihat data cache milik akun sebelumnya (mis. Dashboard/Karyawan)
  // sebelum fetch ulang menimpanya — celah kebocoran data antar sesi. Jalur logout
  // paksa akibat 401 (AUTH_LOGOUT_EVENT di AuthContext) aman tanpa ini karena dia
  // full `window.location.href` reload yang sudah mengosongkan seluruh heap JS.
  const doLogout = () => { queryClient.clear(); logout(); nav2("/login"); };
  // If role is "employee" but user has ANY custom module permission granted (via template),
  // route them to full adminNav filtered by permissions — so they see everything HR ticked.
  // Otherwise "employee" role sees the employee portal. Deteksinya satu sumber dengan
  // App.js (dulu disalin di dua tempat): lib/halamanAwal.js.
  const useEmployeePortal = adalahKaryawanMurni(user);
  const rawSections = useEmployeePortal ? employeeNav : adminNav;
  // Filter sections/items by view permission. Employee portal stays unfiltered.
  // Menu Portal Karyawan hanya masuk akal untuk akun yang benar-benar terhubung
  // ke satu data karyawan. Superadmin/admin tidak punya slip gaji atau absensi
  // sendiri, jadi menampilkannya cuma mengantar mereka ke halaman yang pasti gagal.
  const punyaDataKaryawan = Boolean(user?.employee_id);
  // Sebagian menu adalah pintu ke halaman yang isinya gabungan beberapa modul backend.
  // `mods` = tampil kalau boleh melihat MINIMAL SATU dari daftar itu; `modPrefix` = tampil
  // kalau ada izin eksplisit apa pun di bawah prefix tersebut. Satu module key tunggal
  // (`mod`) tetap dipakai untuk menu yang benar-benar satu modul.
  const bolehLihatItem = (it) => {
    // `boleh` = predikat bersama yang juga dipakai di luar menu (mis. aturan halaman awal
    // di lib/halamanAwal.js). Diperiksa LEBIH DULU supaya item seperti itu tidak pernah
    // diam-diam dinilai ulang lewat daftar `mods` kedua yang berpisah jalan.
    if (typeof it.boleh === "function") return it.boleh(can);
    if (it.modPrefix && Object.keys(user?.permissions || {}).some(k => k.startsWith(it.modPrefix) && can(k, "view"))) {
      return true;
    }
    if (Array.isArray(it.mods)) return it.mods.some(m => can(m, "view"));
    // `modAtauCreate` = tampil kalau boleh MELIHAT ATAU MEMBUAT di modul itu.
    // Dipakai menu yang pemakai hariannya justru cuma punya `create`: usher &
    // frontliner menerbitkan nomor antrian dan melayani tiket, tapi tidak
    // melihat rekap. Digerbangi `view` saja, menu itu hilang justru dari
    // orang yang memakainya sepanjang hari.
    if (it.modAtauCreate) {
      return can(it.modAtauCreate, "view") || can(it.modAtauCreate, "create");
    }
    return !it.mod || can(it.mod, "view");
  };
  const sections = useEmployeePortal
    ? rawSections
    : rawSections
        .map(sec => ({
          ...sec,
          items: sec.items.filter(it =>
            bolehLihatItem(it) &&
            !(it.mod?.startsWith("my_") && !punyaDataKaryawan)
          ),
        }))
        .filter(sec => sec.items.length > 0);
  // Dioper ke halaman lewat Outlet context. Panel tanpa dashboard (BerandaTanpaDashboard)
  // memakainya supaya tidak menyuruh "lihat menu di kiri" kepada akun yang menunya nol.
  // Menu Superadmin (User Management) dirender terpisah di bawah, jadi dihitung sendiri.
  const jumlahMenuTerlihat = sections.reduce((n, s) => n + s.items.length, 0) + (user?.role === "superadmin" ? 1 : 0);

  // ---- Prefetch saat hover menu (cache-warming) ----
  // Sidebar punya puluhan item; kursor yang menyapu ke bawah TIDAK BOLEH menembak
  // puluhan request. Dua pengaman:
  //  1) Jeda niat hover ~200ms — timer cuma menyala kalau kursor DIAM di satu item,
  //     bukan lewat sekilas saat menyapu (dibatalkan di onMouseLeave/hover item lain).
  //  2) Daftar putih EKSPLISIT (`buildPrefetchTargets`) — hanya endpoint GET yang
  //     murah, tanpa efek samping, dan HANYA untuk halaman yang sudah dipindah ke
  //     React Query (lihat catatan migrasi di masing-masing modul halaman). Item
  //     nav yang tidak ada di daftar ini tidak memicu timer/request sama sekali.
  const hoverTimerRef = useRef(null);
  const buildPrefetchTargets = (to) => {
    if (to === "/inventory/produk") {
      return [
        { queryKey: OPSI_QK, queryFn: async () => {
            const [o, g, s] = await Promise.all([
              api.get("/inventory/master-opsi"),
              api.get("/inventory/warehouses").catch(() => null),
              api.get("/supplier").catch(() => null),
            ]);
            return { opsi: o.data, gudang: g?.data.items || [], suppliers: s?.data.items || [] };
          } },
        { queryKey: [PRODUK_QK, PRODUK_DEFAULT_PARAMS],
          queryFn: () => api.get("/inventory/products", { params: PRODUK_DEFAULT_PARAMS }).then(r => r.data) },
      ];
    }
    if (to === "/employees") {
      return [{ queryKey: EMPLOYEES_QK, queryFn: () => api.get("/employees").then(r => r.data) }];
    }
    if (to === "/dashboard/hris") {
      // Key yang SAMA dengan halaman depan — berpindah antar dua dashboard tidak memuat ulang.
      return [{ queryKey: DASHBOARD_QK, queryFn: () => api.get("/dashboard").then(r => r.data) }];
    }
    if (to === "/inventory/dashboard") {
      return [{ queryKey: INVENTORY_DASHBOARD_QK, queryFn: () => api.get("/inventory/dashboard").then(r => r.data) }];
    }
    // Alias Sales (G8 butir b) memuat komponen yang sama dengan "/", jadi cache yang
    // dihangatkan pun sama.
    if (to === "/" || to === ALAMAT_DASHBOARD_SALES) {
      if (useEmployeePortal) {
        return [
          { queryKey: MY_PROFILE_QK, queryFn: () => api.get("/my/profile").then(r => r.data) },
          { queryKey: MY_CUTI_QK, queryFn: () => api.get("/my/cuti").then(r => r.data) },
          { queryKey: MY_SLIPS_QK, queryFn: () => api.get("/my/slips").then(r => r.data) },
        ];
      }
      const targets = [{ queryKey: DASHBOARD_QK, queryFn: () => api.get("/dashboard").then(r => r.data) }];
      // Gerbang izin sama persis dengan Dashboard.jsx — tidak menembak endpoint
      // Finance/Aktivitas untuk staf yang memang tidak berizin melihatnya.
      if (can("finance_dashboard", "view")) {
        targets.push({ queryKey: DASHBOARD_FINANCE_QK, queryFn: () => Promise.all([
          api.get("/finance/dashboard/ceo?months=1"),
          api.get("/finance/piutang/aging-buckets"),
        ]).then(([r1, r2]) => ({ fin: r1.data, aging: r2.data })) });
      }
      if (can("approval_center", "view")) {
        targets.push({ queryKey: DASHBOARD_AKTIVITAS_QK, queryFn: () => api.get("/aktivitas?page_size=10").then(r => r.data) });
      }
      return targets;
    }
    return [];
  };
  const onNavHoverStart = (to) => {
    clearTimeout(hoverTimerRef.current);
    const targets = buildPrefetchTargets(to);
    if (targets.length === 0) return; // tidak di daftar putih — tidak ada timer sama sekali
    hoverTimerRef.current = setTimeout(() => {
      // prefetchQuery menghormati staleTime global (60s) — hover berulang ke menu
      // yang sama tidak menembak ulang kalau cache-nya masih segar.
      targets.forEach(({ queryKey, queryFn }) => {
        queryClient.prefetchQuery({ queryKey, queryFn }).catch(() => {
          // Diam-diam saja — kegagalan prefetch tidak boleh mengganggu apa pun;
          // halaman tetap fetch normal seperti biasa saat benar-benar dibuka.
        });
      });
    }, 200);
  };
  const onNavHoverEnd = () => clearTimeout(hoverTimerRef.current);
  useEffect(() => () => clearTimeout(hoverTimerRef.current), []);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  // Notifikasi (brief bos, laporan performa): <NotificationBell> dulu dipasang DUA KALI
  // di JSX di bawah — satu di sidebar desktop (dark), satu lagi di topbar mobile. Keduanya
  // selalu ter-mount bersamaan; CSS (translate-x / lg:hidden) cuma menyembunyikan secara
  // visual, tidak meng-unmount. Tiap komponen punya useEffect sendiri yang memanggil
  // GET /notifications/count saat mount, jadi SETIAP muat halaman = 2x panggilan nyata
  // (di luar dobel bawaan React.StrictMode saat dev). Sekarang cuma SATU instance yang
  // benar-benar ter-mount, dipilih lewat breakpoint sama persis dengan yang dipakai
  // sidebar/topbar (lg = 1024px) — jadi jumlah instance selalu 1, bukan 2.
  // Breakpoint-nya kini satu sumber (hooks/useApakahDesktop.js) karena FinanceLayout dan
  // MarketingLayout juga memasang lonceng dengan pola satu-instance yang sama.
  const isDesktop = useApakahDesktop();

  // Quick actions injected into the Command Palette (Ctrl+K) — visible to non-employee roles
  // that can access /finance. Skips the sidebar entirely so power users open the Catat wizard
  // pre-filled by jenis (uang_masuk / uang_keluar) from anywhere in the app.
  const paletteSections = useEmployeePortal ? sections : [
    { section: "Aksi Cepat", items: [
      { to: "/finance/catat/uang_masuk",  icon: TrendingUp,   label: "Catat Uang Masuk",  tid: "cmd-quick-uang-masuk" },
      { to: "/finance/catat/uang_keluar", icon: TrendingDown, label: "Catat Uang Keluar", tid: "cmd-quick-uang-keluar" },
      { to: "/finance/catat",             icon: Landmark,     label: "Buka Catat Wizard", tid: "cmd-quick-catat" },
    ]},
    ...sections,
  ];

  // Global keyboard shortcut: Ctrl+K / Cmd+K opens command palette
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(p => !p);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Auto-close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Judul tab browser (brief bos): sebelumnya tidak ada yang pernah mengubah
  // document.title setelah AuthContext memasangnya sekali dari config branding saat app
  // boot — jadi tab selalu bertuliskan judul generik yang sama di semua halaman. Sekarang
  // diperbarui setiap pindah rute, memakai label dari entri NAV (adminNav/employeeNav)
  // yang cocok dengan path aktif. Halaman Finance pakai FinanceLayout, bukan Layout ini,
  // jadi judul tab di sana tidak ikut diperbarui oleh efek ini (di luar cakupan revisi).
  // Dicari di `sections` (SUDAH tersaring izin), bukan daftar mentah: akun tanpa izin
  // Sales yang melihat panel tanpa dashboard di "/" tidak boleh bertab "Dashboard Sales
  // & POS". `alias` karena Sales dirender di "/" DAN di alamat aliasnya (G8 butir b).
  useEffect(() => {
    const allItems = sections.flatMap(sec => sec.items);
    const match = allItems.find(it => it.to === location.pathname || it.alias?.includes(location.pathname));
    const base = branding.app_name || "ZOA OS";
    document.title = match ? `${match.label} — ${base}` : (branding.browser_title || base);
  }, [location.pathname, sections, branding]);

  return (
    <div className="min-h-screen flex bg-[#F8F9FA]">
      {/* Sidebar — desktop always visible, mobile drawer */}
      <aside
        data-testid="sidebar"
        className={`fixed lg:sticky top-0 left-0 z-40 w-64 h-screen shrink-0 bg-[#0F172A] text-white flex flex-col transition-transform duration-300 lg:translate-x-0 ${
          drawerOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            {branding.header_logo_url ? (
              <img src={branding.header_logo_url} alt="logo" className="w-9 h-9 rounded-lg object-cover" />
            ) : (
              <div className="w-9 h-9 rounded-lg bg-emerald-600 grid place-items-center shrink-0">
                <Building2 className="w-5 h-5" />
              </div>
            )}
            <div className="min-w-0">
              <p className="font-display font-bold text-sm leading-tight truncate">{branding.app_name}</p>
              <p className="text-[11px] text-white/50">Group Management</p>
            </div>
          </div>
          <button
            onClick={() => setDrawerOpen(false)} data-testid="btn-close-drawer"
            className="lg:hidden p-1.5 rounded-md hover:bg-white/10 text-white/60 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-3 space-y-4 overflow-y-auto">
          {/* Command palette trigger */}
          <button
            onClick={() => setPaletteOpen(true)}
            data-testid="nav-search-trigger"
            className="w-full flex items-center gap-2 px-3 h-9 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/60 hover:text-white text-sm transition-colors"
          >
            <Search className="w-3.5 h-3.5" />
            <span className="flex-1 text-left">Cari fitur...</span>
            <kbd className="hidden md:inline-flex text-[9px] font-mono text-white/40 px-1 py-0.5 rounded bg-white/5 border border-white/10">Ctrl K</kbd>
          </button>
          {sections.map(({ section, items }) => (
            <div key={section}>
              <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-wider text-white/40">{section}</p>
              <div className="space-y-0.5">
                {items.map(({ to, alias, icon: Icon, label, tid, quickAdd }) => (
                  <div key={to} className="relative group"
                    onMouseEnter={() => onNavHoverStart(to)}
                    onMouseLeave={onNavHoverEnd}
                  >
                    <NavLink
                      to={to} end={to === "/"} data-testid={tid}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 ${quickAdd ? "pr-11" : ""} rounded-lg text-sm transition-colors ${
                          (isActive || alias?.includes(location.pathname))
                            ? "bg-white/10 text-white font-medium"
                            : "text-white/60 hover:text-white hover:bg-white/5"
                        }`
                      }
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      <span className="truncate">{label}</span>
                    </NavLink>
                    {quickAdd && (
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); nav2(quickAdd); }}
                        data-testid={`${tid}-quickadd`}
                        title="Catat Cepat"
                        aria-label="Catat Cepat"
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 grid place-items-center rounded-md bg-gradient-to-br from-amber-400 to-yellow-600 text-black hover:from-amber-300 hover:to-yellow-500 shadow-md ring-1 ring-amber-300/40 transition-all hover:scale-110"
                      >
                        <Plus className="w-3.5 h-3.5" strokeWidth={3} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          {user?.role === "superadmin" && (
            <div>
              <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-wider text-white/40">Superadmin</p>
              <NavLink
                to="/users" data-testid="nav-users"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    isActive
                      ? "bg-white/10 text-white font-medium"
                      : "text-white/60 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <UserCog className="w-4 h-4 shrink-0" />
                <span>User Management</span>
              </NavLink>
            </div>
          )}
        </nav>

        <div className="px-3 py-3 border-t border-white/10 space-y-1.5">
          <div className="flex items-center gap-2 px-3 py-2">
            {user?.employee_id ? (
              <EmployeeAvatar employeeId={user.employee_id} nama={user.name} size={36} />
            ) : (
              <div className="w-9 h-9 rounded-full bg-emerald-600/20 text-emerald-300 grid place-items-center text-sm font-semibold">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-[11px] text-white/50 capitalize">{user?.role}</p>
            </div>
            {/* Tetap 1 instance saat muat halaman biasa (isDesktop saja); instance kedua
                cuma muncul saat drawer mobile dibuka, karena topbar mobile (z-20) ketutup
                backdrop (z-30) sehingga lonceng di topbar jadi tak tersentuh. */}
            {(isDesktop || drawerOpen) && <NotificationBell dark />}
          </div>
          <button
            data-testid="btn-logout"
            onClick={() => { if (confirm("Keluar dari akun ini?")) doLogout(); }}
            className="w-full inline-flex items-center gap-2 px-3 py-2.5 rounded-lg bg-rose-600/10 hover:bg-rose-600/25 text-rose-300 hover:text-rose-100 text-sm font-medium transition-colors border border-rose-500/20 hover:border-rose-500/50"
          >
            <LogOut className="w-4 h-4" />
            Keluar / Ganti User
          </button>
        </div>
      </aside>

      {/* Backdrop for mobile drawer */}
      {drawerOpen && (
        <div
          onClick={() => setDrawerOpen(false)}
          className="fixed inset-0 z-30 bg-black/50 backdrop-blur-sm lg:hidden"
          data-testid="drawer-backdrop"
        />
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile topbar */}
        <header className="lg:hidden sticky top-0 z-20 bg-white border-b border-slate-200 flex items-center gap-3 px-4 py-3">
          <button
            onClick={() => setDrawerOpen(true)} data-testid="btn-open-drawer"
            className="p-2 -ml-2 rounded-md hover:bg-slate-100 text-slate-700"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {branding.header_logo_url ? (
              <img src={branding.header_logo_url} alt="logo" className="w-7 h-7 rounded-md object-cover shrink-0" />
            ) : (
              <div className="w-7 h-7 rounded-md bg-emerald-600 grid place-items-center shrink-0">
                <Building2 className="w-4 h-4 text-white" />
              </div>
            )}
            <p className="font-display font-bold text-sm text-slate-900 truncate">{branding.app_name}</p>
          </div>
          {!isDesktop && <NotificationBell />}
          {user?.employee_id ? (
            <EmployeeAvatar employeeId={user.employee_id} nama={user.name} size={30} />
          ) : (
            <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center text-xs font-semibold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
          )}
          <button
            data-testid="btn-logout-mobile"
            onClick={() => { if (confirm("Keluar dari akun ini?")) doLogout(); }}
            title="Keluar / Ganti User"
            className="p-2 rounded-md text-rose-600 hover:bg-rose-50 border border-rose-200"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </header>

        <main className="flex-1 overflow-x-hidden">
          {/* `keluar` = doLogout yang sama dengan tombol sidebar (membersihkan cache
              React Query) — panel tanpa dashboard untuk akun tanpa menu memakainya. */}
          <Outlet context={{ jumlahMenuTerlihat, keluar: doLogout }} />
        </main>
        {branding.show_footer !== false && (
          <footer data-testid="app-footer" className="border-t border-slate-200 bg-white px-4 md:px-8 py-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
            <div className="flex items-center gap-2">
              {branding.footer_logo_url && (
                <img src={branding.footer_logo_url} alt="" className="h-5 w-auto object-contain" />
              )}
              <span>{branding.footer_text}</span>
            </div>
            <span className="font-mono text-[10px] text-slate-400">{branding.app_name}</span>
          </footer>
        )}
      </div>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} sections={paletteSections} />
    </div>
  );
}
